import './styles.scss'
import React, { useState, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { SelectButton } from 'primereact/selectbutton'
import { Button } from 'primereact/button'
import { updateNewsReminder } from 'Services/Supabase'
import { Calendar } from 'primereact/calendar'
import { commonMethods } from 'Utils/commonMethods'

const REPEAT_OPTIONS = [
  { label: 'Once', value: 'once' },
  { label: 'Mon - Fri', value: 'weekdays' },
  { label: 'Custom', value: 'custom' }
]

const DAYS_OF_WEEK = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 }
]

const EditReminderModal = ({ visible, reminder, userId, onClose, onSuccess }) => {
  const { utcTimeToLocalTimeString, localTimeStringToUtcTime, timeStringToDate, dateToTimeString } = commonMethods

  const [title, setTitle] = useState('')
  const [prompt, setPrompt] = useState('')
  const [timeValue, setTimeValue] = useState('09:00')
  const [repeatType, setRepeatType] = useState('daily')
  const [customDays, setCustomDays] = useState([])
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  const canEditTitle = reminder?.can_edit_title !== false
  const canEditPrompt = reminder?.can_edit_prompt !== false
  const canEditTime = reminder?.can_edit_time !== false

  useEffect(() => {
    if (!reminder) return
    setTitle(reminder.title || '')
    setPrompt(reminder.prompt || '')
    const nextRunAt = reminder?.next_run_at

    if (nextRunAt) {
      const d = new Date(nextRunAt)

      setTimeValue(Number.isNaN(d.getTime()) ? utcTimeToLocalTimeString(reminder.reminder_time || reminder.global_reminder_time) : dateToTimeString(d))
    } else {
      setTimeValue(utcTimeToLocalTimeString(reminder.reminder_time || reminder.global_reminder_time))
    }
    setRepeatType(reminder.repeat_type || 'daily')
    const days = Array.isArray(reminder.custom_days) ? reminder.custom_days : []

    setCustomDays(days)
    // Use saved value; default to ON when not explicitly false
    setIsActive(reminder.is_active !== false)
  }, [reminder, visible])
  const isSubmitDisabled = saving || (canEditTitle && !title.trim()) || (canEditPrompt && !prompt.trim())

  const handleUpdate = async () => {
    if (isSubmitDisabled || !reminder?.id || !userId) return
    setSaving(true)
    try {
      const payload = {
        p_reminder_id: reminder.id,
        p_user_id: userId,
        p_title: canEditTitle ? title : null,
        p_prompt: canEditPrompt ? prompt : null,
        p_reminder_time: canEditTime ? localTimeStringToUtcTime(timeValue) : null,
        p_repeat_type: repeatType,
        p_custom_days: repeatType === 'custom' ? customDays : [],
        p_is_active: isActive
      }
      const { success } = await updateNewsReminder(payload)

      if (success) {
        onSuccess?.()
        onClose?.()
      }
    } catch (e) {
      console.warn('EditReminderModal: update failed', e)
    } finally {
      setSaving(false)
    }
  }

  const handleRepeatChange = (nextType) => {
    setRepeatType(nextType)
  }

  const handleToggleDay = (dayValue) => {
    setCustomDays((prev) => {
      const exists = prev.includes(dayValue)

      if (exists) {
        return prev.filter((d) => d !== dayValue)
      }

      return [...prev, dayValue].sort((a, b) => a - b)
    })
  }

  if (!reminder) return null

  return (
    <Dialog visible={visible} onHide={onClose} className="edit-reminder-modal" contentClassName="edit-reminder-modal__content" header="Update History" closable dismissableMask blockScroll style={{ width: '560px' }}>
      <div className="edit-reminder-modal__body edit-reminder-modal__section--template">
        <div className="edit-reminder-modal__section">
          <InputText value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canEditTitle} className="edit-reminder-modal__input" placeholder="Template Title" />
        </div>

        <div className="edit-reminder-modal__section edit-reminder-modal__section--prompt">
          <InputTextarea value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={!canEditPrompt} className="edit-reminder-modal__textarea" rows={4} placeholder="Give me a concise morning brief..." />
        </div>

        <div className="edit-reminder-modal__section edit-reminder-modal__section--time">
          <div className="edit-reminder-modal__label">
            <i className="pi pi-clock edit-reminder-modal__clock-icon" />
            Time
          </div>
          <div className="edit-reminder-modal__time-row">
            <Calendar value={timeStringToDate(timeValue)} onChange={(e) => setTimeValue(dateToTimeString(e.value))} appendTo="self" showIcon timeOnly icon={() => <i className="pi pi-clock" />} disabled={!canEditTime} stepMinute={5} hourFormat="12" readOnlyInput />
          </div>
        </div>

        <div className="edit-reminder-modal__section">
          <div className="edit-reminder-modal__label edit-reminder-modal__label--center">Repeat Mode</div>
          <SelectButton value={repeatType} options={REPEAT_OPTIONS} onChange={(e) => handleRepeatChange(e.value)} className="edit-reminder-modal__repeat" />
        </div>

        {repeatType === 'custom' && (
          <div className="edit-reminder-modal__section edit-reminder-modal__section--custom-days">
            <div className="edit-reminder-modal__days">
              {DAYS_OF_WEEK.map((day) => {
                const selected = customDays.includes(day.value)

                return (
                  <button key={day.value} type="button" className={`edit-reminder-modal__day${selected ? ' edit-reminder-modal__day--selected' : ''}`} onClick={() => handleToggleDay(day.value)}>
                    <span className="edit-reminder-modal__day-label">{day.label}</span>
                    {selected && <i className="pi pi-check edit-reminder-modal__day-check" />}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <div className="edit-reminder-modal__section edit-reminder-modal__section--row">
          <div htmlFor="active-switch" className="edit-reminder-modal__label">
            Active
          </div>
          <button type="button" className={`edit-reminder-modal__toggle${isActive ? ' edit-reminder-modal__toggle--on' : ' edit-reminder-modal__toggle--off'}`} onClick={() => setIsActive((prev) => !prev)} aria-pressed={isActive}>
            <span className="edit-reminder-modal__toggle-label">{isActive ? 'ON' : 'OFF'}</span>
            <span className="edit-reminder-modal__toggle-knob" />
          </button>
        </div>

        <Button label="Update" className="edit-reminder-modal__update-btn" onClick={handleUpdate} loading={saving} disabled={saving} />
      </div>
    </Dialog>
  )
}

export default EditReminderModal
